import type { TenantAssetKind } from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { toast } from "@loot-os/ui/components/sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@loot-os/ui/components/tabs";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useRouter, useSearch } from "@tanstack/react-router";
import { useState } from "react";

import { StorefrontBannersEditor } from "../components/StorefrontBannersEditor";
import { RasterImageField } from "../components/StorefrontFields";
import { SectionSwitch, StorefrontSection } from "../components/StorefrontSection";
import { StorefrontTerminologyEditor } from "../components/StorefrontTerminologyEditor";
import { StorefrontThemeEditor } from "../components/StorefrontThemeEditor";
import { useStorefrontConfig } from "../lib/storefront";
import {
  isStorefrontTab,
  resolveStorefrontTab,
  STOREFRONT_TAB_LABELS,
  STOREFRONT_TABS,
  type StorefrontSearch,
  type StorefrontTab,
} from "../lib/storefrontTabs";
import {
  TENANT_ASSET_FIELDS,
  type TenantAssetUrlField,
  uploadTenantAssetFile,
} from "../lib/tenantAssets";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";

const IMAGE_FIELDS = [
  {
    kind: "logo",
    label: "Logo",
    previewClassName: "h-8 max-w-14 object-contain",
    help: "Shown in the storefront header. PNG, JPEG, WEBP or AVIF, up to 5 MB — not SVG.",
  },
  {
    kind: "favicon",
    label: "Favicon",
    previewClassName: "size-5 object-contain",
    help: "Browser tab icon. PNG, JPEG, WEBP or AVIF, up to 5 MB — not SVG.",
  },
  {
    kind: "badge",
    label: "Badge",
    previewClassName: "size-10 object-contain",
    help: "Small mark distinct from the logo. PNG, JPEG, WEBP or AVIF, up to 5 MB — not SVG.",
  },
] as const;

/**
 * The storefront's front page: whether it serves (the switch on the header, saved on flip, the
 * way Payments, Battles and Support chat carry theirs), then a tab each for General — the name
 * and artwork it serves under — Banners, its scheduled slides, Theme, and Terminology, its
 * nouns. The tab rides `?tab=` (`storefrontTabs.ts`), so a tab is a link. Every other section is
 * a page of its own beneath this one in the sidebar. Every tab holds an unsaved draft outside a
 * dialog, so all stay mounted: a look at the other tab never discards a half-typed display name
 * or corner radius. The branding form and the theme editor each run their own
 * `useStorefrontConfig`, so a flip of the switch, or a save on the other tab, never fires their
 * `onSaved` and drops an edit in progress.
 */
export function StorefrontGeneralPage() {
  const router = useRouter();
  const { tenantId } = useParams({ strict: false }) as { tenantId: string };
  const search = useSearch({ strict: false }) as StorefrontSearch;
  const activeTab = resolveStorefrontTab(search.tab);
  const { query, update } = useStorefrontConfig();
  const { can } = useTenantRole();

  const setTab = (tab: StorefrontTab) => {
    void router.navigate({
      to: "/tenants/$tenantId/storefront",
      params: { tenantId },
      search: { tab },
      replace: true,
    });
  };

  // Narrowing only: the layout renders a section once the same query has data.
  if (!query.data) return null;
  const { config, environment } = query.data;

  return (
    <StorefrontSection
      title="Storefront"
      description={
        environment.type === "sandbox"
          ? "Hosted storefront configuration for this sandbox — independent of the live store. Off serves nothing on its domains until it is switched back on."
          : "Hosted storefront configuration for this tenant's live environment. Off serves nothing on its domains until it is switched back on."
      }
      environment={environment}
      aside={
        <SectionSwitch
          id="storefront-enabled"
          label="Storefront"
          checked={config.enabled}
          disabled={update.isPending || !can("editor")}
          onCheckedChange={(checked) => update.mutate({ enabled: checked })}
        />
      }
    >
      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          if (isStorefrontTab(value)) setTab(value);
        }}
        className="gap-4"
      >
        <TabsList>
          {STOREFRONT_TABS.map((tab) => (
            <TabsTrigger key={tab} value={tab}>
              {STOREFRONT_TAB_LABELS[tab]}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="general" keepMounted>
          <BrandingCard />
        </TabsContent>

        <TabsContent value="banners" keepMounted>
          <StorefrontBannersEditor />
        </TabsContent>

        <TabsContent value="theme" keepMounted>
          <StorefrontThemeEditor />
        </TabsContent>

        <TabsContent value="terminology" keepMounted>
          <StorefrontTerminologyEditor />
        </TabsContent>
      </Tabs>
    </StorefrontSection>
  );
}

function BrandingCard() {
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [faviconUrl, setFaviconUrl] = useState<string | null>(null);
  const [badgeUrl, setBadgeUrl] = useState<string | null>(null);
  // Which image an upload is running for, from the pick until the server has checked the file.
  const [uploadingKind, setUploadingKind] = useState<TenantAssetKind | null>(null);

  const { query, update, queryOptions } = useStorefrontConfig({
    onSaved: () => {
      setDisplayName(null);
      setLogoUrl(null);
      setFaviconUrl(null);
      setBadgeUrl(null);
    },
  });
  const { can } = useTenantRole();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const startUpload = useMutation(trpc.storefront.startAssetUpload.mutationOptions());
  const finishUpload = useMutation(
    trpc.storefront.finishAssetUpload.mutationOptions({
      onSuccess: (result, input) => {
        const field = TENANT_ASSET_FIELDS[input.kind];
        queryClient.setQueryData(queryOptions.queryKey, (old) =>
          old ? { ...old, config: { ...old.config, [field]: result.publicUrl } } : old,
        );
        if (input.kind === "logo") setLogoUrl(null);
        else if (input.kind === "favicon") setFaviconUrl(null);
        else setBadgeUrl(null);
        toast.success("Image uploaded.");
      },
    }),
  );

  if (!query.data) return null;

  const { config, assetsAvailable } = query.data;
  const currentDisplayName = displayName ?? config.displayName;
  const urls = {
    logoUrl: logoUrl ?? config.logoUrl,
    faviconUrl: faviconUrl ?? config.faviconUrl,
    badgeUrl: badgeUrl ?? config.badgeUrl,
  };
  const setUrl: Record<TenantAssetUrlField, (value: string) => void> = {
    logoUrl: setLogoUrl,
    faviconUrl: setFaviconUrl,
    badgeUrl: setBadgeUrl,
  };

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    update.mutate({
      displayName: currentDisplayName,
      logoUrl: urls.logoUrl.trim(),
      faviconUrl: urls.faviconUrl.trim(),
      badgeUrl: urls.badgeUrl.trim(),
    });
  };

  const pick = async (kind: (typeof IMAGE_FIELDS)[number]["kind"], file: File) => {
    setUploadingKind(kind);
    try {
      await uploadTenantAssetFile(
        file,
        (input) => startUpload.mutateAsync({ kind, ...input }),
        (uploadKey) => finishUpload.mutateAsync({ kind, uploadKey }),
      );
    } finally {
      setUploadingKind(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Branding</CardTitle>
        <CardDescription>
          The name and artwork shoppers see on this storefront. The currency has its own section.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid gap-5" onSubmit={save}>
          {!assetsAvailable && (
            <p className="text-sm text-muted-foreground">
              Image uploads are not available on this stage. You can still paste HTTPS URLs.
            </p>
          )}
          <div className="grid gap-2">
            <Label htmlFor="storefront-display-name">Display name</Label>
            <Input
              id="storefront-display-name"
              type="text"
              value={currentDisplayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Falls back to the tenant name"
            />
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            {IMAGE_FIELDS.map((field) => {
              const urlField = TENANT_ASSET_FIELDS[field.kind];
              return (
                <RasterImageField
                  key={field.kind}
                  id={`storefront-${field.kind}`}
                  label={field.label}
                  url={urls[urlField]}
                  onUrlChange={setUrl[urlField]}
                  onFile={(file) => pick(field.kind, file)}
                  uploading={uploadingKind === field.kind}
                  disabled={!can("editor") || update.isPending}
                  uploadsAvailable={assetsAvailable}
                  previewClassName={field.previewClassName}
                  help={field.help}
                />
              );
            })}
          </div>

          <div>
            <Button type="submit" disabled={update.isPending || !can("editor")}>
              {update.isPending ? "Saving…" : "Save branding"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
